/**
 * CommandSuggestions - Autocomplete suggestions dropdown
 */

import React from 'react';
import {
  AlertTriangle,
  Calendar,
  BarChart,
  Users,
  FileText,
  Send,
  Clock,
  User,
  Building2,
  Briefcase,
  Loader2,
  Search,
  Bell,
} from 'lucide-react';
import { Pressable } from '../../common/motionSystem';

// Icon mapping for templates
const templateIcons = {
  show_workers: Users,
  show_incidents: AlertTriangle,
  show_training: FileText,
  show_projects: Building2,
  show_assets: Briefcase,
  show_reports: FileText,
  show_schedule: Calendar,
  show_analytics: BarChart,
  show_foreman: Users,
  notify_workers: Send,
  schedule_meeting: Calendar,
};

// Icon mapping for entity types
const entityIcons = {
  worker: User,
  project: Building2,
  crew: Users,
  site: Briefcase,
};

const CommandSuggestions = ({
  suggestions,
  selectedIndex,
  onSelect,
  loading,
  input,
}) => {
  const { templates, entities, recent } = suggestions;

  // Calculate which suggestion is selected
  let currentIndex = 0;
  const getIsSelected = () => {
    const idx = currentIndex;
    currentIndex += 1;
    return idx === selectedIndex;
  };

  if (loading) {
    return (
      <div className="command-bar-loading">
        <Loader2 size={20} />
        <span>Searching...</span>
      </div>
    );
  }

  const hasResults = templates.length > 0 || entities.length > 0 || recent.length > 0;

  if (input && !hasResults) {
    return (
      <div className="command-bar-empty">
        <Search size={24} />
        <span className="command-bar-empty-text">
          No matching commands or results
        </span>
      </div>
    );
  }

  return (
    <div className="command-bar-suggestions">
      {/* Command Templates */}
      {templates.length > 0 && (
        <div className="command-bar-section">
          <div className="command-bar-section-title">
            {input ? 'Matching Commands' : 'Suggested Commands'}
          </div>
          {templates.map((template, idx) => {
            const Icon = templateIcons[template.id] || Bell;
            const isSelected = getIsSelected();

            return (
              <Pressable
                as="div"
                key={template.id}
                className={`command-bar-suggestion-item ${isSelected ? 'selected' : ''}`}
                onClick={() => onSelect({ ...template, suggestionType: 'template' }, idx)}
              >
                <div className="command-bar-suggestion-icon">
                  <Icon size={16} />
                </div>
                <div className="command-bar-suggestion-content">
                  <div className="command-bar-suggestion-title">
                    {template.pattern}
                  </div>
                  <div className="command-bar-suggestion-desc">
                    {template.description}
                  </div>
                </div>
                <span className="command-bar-suggestion-badge">
                  {template.category}
                </span>
              </Pressable>
            );
          })}
        </div>
      )}

      {/* Entity Suggestions */}
      {entities.length > 0 && (
        <div className="command-bar-section">
          <div className="command-bar-section-title">Suggestions</div>
          {entities.map((entity, idx) => {
            const Icon = entityIcons[entity.type] || User;
            const isSelected = getIsSelected();

            return (
              <Pressable
                as="div"
                key={entity.id}
                className={`command-bar-suggestion-item ${isSelected ? 'selected' : ''}`}
                onClick={() => onSelect({ ...entity, suggestionType: 'entity' }, templates.length + idx)}
              >
                <div className="command-bar-suggestion-icon">
                  <Icon size={16} />
                </div>
                <div className="command-bar-suggestion-content">
                  <div className="command-bar-suggestion-title">
                    {entity.name}
                  </div>
                  <div className="command-bar-suggestion-desc">
                    {entity.role && `${entity.role} • `}
                    {entity.site || entity.type}
                  </div>
                </div>
                <span className="command-bar-suggestion-badge">
                  {entity.type}
                </span>
              </Pressable>
            );
          })}
        </div>
      )}

      {/* Recent Commands */}
      {recent.length > 0 && (
        <div className="command-bar-section">
          <div className="command-bar-section-title">Recent</div>
          {recent.map((cmd, idx) => {
            const isSelected = getIsSelected();

            return (
              <Pressable
                as="div"
                key={cmd.id}
                className={`command-bar-suggestion-item ${isSelected ? 'selected' : ''}`}
                onClick={() => onSelect({ ...cmd, suggestionType: 'recent' }, templates.length + entities.length + idx)}
              >
                <div className="command-bar-suggestion-icon">
                  <Clock size={16} />
                </div>
                <div className="command-bar-suggestion-content">
                  <div className="command-bar-suggestion-title">
                    {cmd.query}
                  </div>
                  {cmd.resultCount && (
                    <div className="command-bar-suggestion-desc">
                      {cmd.resultCount} results
                    </div>
                  )}
                </div>
                <span className="command-bar-suggestion-meta">
                  {cmd.timestamp}
                </span>
              </Pressable>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CommandSuggestions;
